import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { isSessionExpiry, SESSION_EXPIRED_NOTICE } from './sessionExpiry';

// One place that turns a dropped session into a clean sign-out.
//
// There is no shared axios instance in this app — 38 components call bare
// `axios` with an inline auth header — so the only single point where every
// authenticated request's 401 can be caught is a global RESPONSE interceptor.
// This mounts once, inside the Router (so it has navigation), and registers it.
//
// On a session-expiry 401 (see sessionExpiry.js for why that is the header, not
// the message) it clears the stored token and sends the athlete to the login
// screen with a readable notice — replacing the bare "Invalid token" banner they
// used to be stranded on. `logout()` flipping isAuthenticated is what makes
// PrivateRoute the backstop; the explicit navigate is what carries the message.
//
// The error is still rejected, not swallowed, so callers' `finally` blocks run
// and nothing downstream is silently starved of its rejection. The redirect
// (replace:true) unmounts the private page before its own catch can paint.

const SessionGuard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (isSessionExpiry(error)) {
          logout();
          navigate('/login', {
            state: { notice: SESSION_EXPIRED_NOTICE },
            replace: true,
          });
        }
        return Promise.reject(error);
      }
    );

    // Eject on unmount. CRA's dev double-mount would otherwise stack a second
    // interceptor and fire logout+navigate twice for every 401.
    return () => axios.interceptors.response.eject(id);
  }, [navigate, logout]);

  return null;
};

export default SessionGuard;
